import { StateCreator } from 'zustand';
import { allSlices, boardSlice, BoardState, historyItem } from './interface';
import { socket } from '@/services/socket';
import socketEvents, { frontendEvents } from '@/utils/socketEvents';
import { htmlToImage, isImageData, isTextData, sourceToImage } from '@/utils/common';
import { COLORS } from '@/utils/constants';

const initialBoardState: BoardState = {
  id: 1,
  shapes: [],
  boardName: 'Page 1',
  history: [],
  undoIndex: -1,
  grid: 'none',
  backgroundStyle: window.location.href.includes('whiteboard') ? COLORS.WHITEBOARD.gray : '#000000'
};

export const createBoardSlice: StateCreator<allSlices, [], [], boardSlice> = (set, get) => ({
  initialBoardState,
  current: 0,
  previewBoard: null,
  boards: [JSON.parse(JSON.stringify(initialBoardState))],
  selectedShape: null,
  currentEvent: null,
  boardInit: [],
  totalBoards: 1,
  printed: 0,
  addPDF: async (images) =>
    set((state) => {
      const currentBoards = [...state.board.boards];
      const current = state.board.current;
      let totalBoards = state.board.totalBoards;

      // send set background to image
      if (state.room.userId === state.room.presenterId) {
        socket.emit(socketEvents.board.DRAW, {
          roomId: state.room.roomId,
          userId: state.room.userId,
          boardData: {
            name: frontendEvents.background.PDF,
            images,
            boardId: totalBoards + 1,
            current
          },
          save: true
        });
      }

      const pdfBoards = images.map(
        ({ name, url }) =>
          ({
            ...JSON.parse(JSON.stringify(state.board.initialBoardState)),
            id: ++totalBoards,
            backgroundStyle: url,
            boardName: name,
            grid: 'none'
          }) as BoardState
      );

      currentBoards.splice(current + 1, 0, ...pdfBoards);

      return {
        board: {
          ...state.board,
          boards: currentBoards,
          current: current + 1,
          totalBoards
        }
      };
    }),
  addPrinted: () =>
    set((state) => ({ board: { ...state.board, printed: state.board.printed + 1 } })),
  addBoard: (boardName) =>
    set((state) => {
      const currentBoards = [...state.board.boards];

      if (state.room.userId === state.room.presenterId) {
        socket.emit(socketEvents.board.DRAW, {
          roomId: state.room.roomId,
          userId: state.room.userId,
          boardData: {
            name: frontendEvents.board.ADD,
            ...(boardName && { boardName, boardId: state.board.totalBoards + 1 }),
            current: state.board.current + 1
          },
          save: true
        });
      }

      const newBoardData: BoardState = {
        ...JSON.parse(JSON.stringify(state.board.initialBoardState)),
        id: state.board.totalBoards + 1,
        boardName: boardName || `Page ${state.board.totalBoards + 1}`
      };

      currentBoards.splice(state.board.current + 1, 0, newBoardData);

      return {
        board: {
          ...state.board,
          boards: currentBoards,
          current: state.board.current + 1,
          totalBoards: state.board.totalBoards + 1
        }
      };
    }),
  setBoard: (index) =>
    set((state) => {
      if (state.room.userId === state.room.presenterId) {
        socket.emit(socketEvents.board.DRAW, {
          roomId: state.room.roomId,
          userId: state.room.userId,
          boardData: { name: frontendEvents.board.CHANGE, current: index },
          save: true
        });
      }
      return {
        board: { ...state.board, current: index }
      };
    }),
  deleteBoard: (id) =>
    set((state) => {
      const boards = state.board.boards;

      if (boards.length <= 1) return state;

      const deleteIndex = boards.findIndex((b) => b.id === id);
      if (deleteIndex === -1) return state;

      let current = state.board.current;

      if (deleteIndex < current) {
        current -= 1;
      } else if (deleteIndex === current) {
        current = Math.max(0, current - 1);
      }

      const updatedBoards = boards.filter((b) => b.id !== id);

      if (state.room.userId === state.room.presenterId) {
        socket.emit(socketEvents.board.DRAW, {
          roomId: state.room.roomId,
          userId: state.room.userId,
          boardData: {
            name: frontendEvents.board.DELETE,
            boardId: id,
            current
          },
          save: true
        });
      }

      return {
        board: {
          ...state.board,
          boards: updatedBoards,
          current
        }
      };
    }),
  setBoardState: (data) =>
    set((state) => ({
      board: { ...state.board, ...data }
    })),
  setAllShapes: (shapes) =>
    set((state) => {
      const newBoardShape = [...state.board.boards];
      newBoardShape[state.board.current].shapes = shapes;

      return {
        board: {
          ...state.board,
          boards: newBoardShape
        }
      };
    }),
  setShape: (shape, save, previousShape) =>
    set((state) => {
      const currentBoards = [...state.board.boards];
      const currentBoard = currentBoards[state.board.current];
      if (currentBoard) {
        const currentShapes = [...currentBoard.shapes];
        const index = currentShapes.findIndex((d) => d.id === shape.id);

        if (index === -1) {
          currentShapes.push(shape);
          if (save) {
            const happens: historyItem = {
              id: shape.id,
              currentShape: shape,
              previousShape: null,
              shapeIndex: currentShapes.length - 1
            };
            currentBoard.history = currentBoard.history.slice(0, currentBoard.undoIndex + 1);
            currentBoard.history.push(happens);
            currentBoard.undoIndex += 1;
          }
        } else {
          currentShapes[index] = shape;
          if (save && previousShape) {
            const happens: historyItem = {
              id: shape.id,
              previousShape,
              currentShape: shape,
              shapeIndex: index
            };
            currentBoard.history = currentBoard.history.slice(0, currentBoard.undoIndex + 1);
            currentBoard.history.push(happens);
            currentBoard.undoIndex += 1;
          }
        }
        currentBoard.shapes = currentShapes;
        return {
          board: {
            ...state.board,
            boards: currentBoards
          }
        };
      }
      return state;
    }),
  clearCanvas: () =>
    set((state) => {
      const currentBoards = [...state.board.boards];
      const currentBoard = currentBoards[state.board.current];

      if (currentBoard) {
        currentBoards[state.board.current].shapes = [];
        currentBoards[state.board.current].history = [];
        currentBoards[state.board.current].undoIndex = -1;
        if (state.room.userId === state.room.presenterId) {
          socket.emit(socketEvents.board.DRAW, {
            roomId: state.room.roomId,
            userId: state.room.userId,
            boardData: {
              name: frontendEvents.board.CLEAR,
              boardId: currentBoard.id
            },
            save: true
          });
        }
        return {
          board: {
            ...state.board,
            boardShapes: currentBoards
          }
        };
      }
      return state;
    }),
  setCurrentEvent: (event) =>
    set((state) => {
      return {
        board: {
          ...state.board,
          currentEvent: event
        }
      };
    }),
  setBoardInit: (event) =>
    set((state) => {
      return {
        board: {
          ...state.board,
          boardInit: event
        }
      };
    }),
  undo: async () => {
    const staleState = get();
    const boards = [...staleState.board.boards];
    const currentBoard = boards[staleState.board.current];

    const { history } = currentBoard;

    if (currentBoard.undoIndex < 0) return;

    if (staleState.room.userId === staleState.room.presenterId && !staleState.board.currentEvent) {
      socket.emit(socketEvents.board.DRAW, {
        roomId: staleState.room.roomId,
        userId: staleState.room.userId,
        boardData: {
          name: frontendEvents.draw.UNDO,
          boardId: currentBoard.id
        },
        save: true
      });
    }

    const action = history[currentBoard.undoIndex];

    currentBoard.undoIndex -= 1;

    set({
      board: {
        ...staleState.board,
        boards
      }
    });

    const { shapeIndex } = action;

    if (!action.previousShape) {
      action.currentShape = currentBoard.shapes[shapeIndex];
      boards[staleState.board.current].shapes = currentBoard.shapes.filter(
        (shape) => shape.id !== action.id
      );
    } else {
      if (isTextData(action.previousShape)) {
        if (typeof action.previousShape.element.width === 'undefined') {
          const shape = await htmlToImage(action.previousShape);
          if (shape) action.previousShape = shape;
        }
      } else if (isImageData(action.previousShape)) {
        if (typeof action.previousShape.element.width === 'undefined') {
          const shape = await sourceToImage(action.previousShape);
          if (shape) action.previousShape = shape;
        }
      }

      if (shapeIndex > -1 && action.currentShape) {
        action.currentShape = currentBoard.shapes[shapeIndex];
        currentBoard.shapes[shapeIndex] = action.previousShape;
      } else if (action.currentShape === null) {
        currentBoard.shapes.splice(action.shapeIndex, 0, action.previousShape);
      }
    }

    return set((state) => {
      if (
        state.board.boards[state.board.current].undoIndex ===
        staleState.board.boards[staleState.board.current].undoIndex
      )
        return {
          board: {
            ...state.board,
            boards
          }
        };
      else {
        state.board.boards[staleState.board.current].history = history;
        return state;
      }
    });
  },
  redo: async () => {
    const staleState = get();
    const boards = [...staleState.board.boards];
    const currentBoard = boards[staleState.board.current];
    const { history } = currentBoard;

    if (currentBoard.undoIndex >= history.length - 1) return;

    if (staleState.room.userId === staleState.room.presenterId && !staleState.board.currentEvent) {
      socket.emit(socketEvents.board.DRAW, {
        roomId: staleState.room.roomId,
        userId: staleState.room.userId,
        boardData: {
          name: frontendEvents.draw.REDO,
          boardId: currentBoard.id
        },
        save: true
      });
    }

    currentBoard.undoIndex += 1;

    set({
      board: {
        ...staleState.board,
        boards
      }
    });

    const action = history[currentBoard.undoIndex];
    const { shapeIndex } = action;

    if (!action.currentShape) {
      action.previousShape = currentBoard.shapes[shapeIndex];
      boards[staleState.board.current].shapes = currentBoard.shapes.filter(
        (shape) => shape.id !== action.id
      );
    } else {
      if (isTextData(action.currentShape)) {
        if (typeof action.currentShape.element.width === 'undefined') {
          const shape = await htmlToImage(action.currentShape);
          if (shape) action.currentShape = shape;
        }
      } else if (isImageData(action.currentShape)) {
        if (typeof action.currentShape.element.width === 'undefined') {
          const shape = await sourceToImage(action.currentShape);
          if (shape) action.currentShape = shape;
        }
      }

      if (shapeIndex > -1 && action.previousShape) {
        action.previousShape = currentBoard.shapes[shapeIndex];
        currentBoard.shapes[shapeIndex] = action.currentShape;
      } else if (action.previousShape === null) {
        currentBoard.shapes.splice(action.shapeIndex, 0, action.currentShape);
      }
    }

    return set((state) => {
      if (
        state.board.boards[state.board.current].undoIndex ===
        staleState.board.boards[staleState.board.current].undoIndex
      )
        return {
          board: {
            ...state.board,
            boards
          }
        };
      else {
        state.board.boards[staleState.board.current].history = history;
        return state;
      }
    });
  },
  setBackgroundGrid: (grid) =>
    set((state) => {
      const currentBoards = [...state.board.boards];
      const currentBoard = currentBoards[state.board.current];
      if (currentBoard) {
        currentBoard.grid = grid;
        if (state.room.userId === state.room.presenterId) {
          socket.emit(socketEvents.board.DRAW, {
            roomId: state.room.roomId,
            userId: state.room.userId,
            boardData: {
              name: frontendEvents.background.GRID,
              backgroundGrid: grid,
              boardId: currentBoard.id
            },
            save: true
          });
        }
        return {
          board: {
            ...state.board,
            boards: currentBoards,
            initialBoardState: { ...state.board.initialBoardState, grid }
          }
        };
      }
      return state;
    }),
  setBackgroundstyle: (background) =>
    set((state) => {
      const currentBoards = [...state.board.boards];
      const currentBoard = currentBoards[state.board.current];
      if (currentBoard) {
        currentBoard.backgroundStyle = background;

        if (
          state.room.userId === state.room.presenterId &&
          state.room.roomId &&
          state.room.userId
        ) {
          socket.emit(socketEvents.board.DRAW, {
            roomId: state.room.roomId,
            userId: state.room.userId,
            boardData: {
              name: frontendEvents.background.BACKGROUND,
              backgroundstyle: background,
              boardId: currentBoard.id
            },
            save: true
          });
        }

        let newBackground = background;

        if (background.includes('https://')) {
          newBackground = state.board.initialBoardState.backgroundStyle;
        }

        return {
          board: {
            ...state.board,
            boards: currentBoards,
            initialBoardState: { ...state.board.initialBoardState, backgroundStyle: newBackground }
          }
        };
      }
      return state;
    })
});

export default createBoardSlice;
